import React, { useState, useEffect } from "react";
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdEvent, 
  MdSpeed, 
  MdTimelapse, 
  MdAssignment,
  MdCheckCircle,
  MdHistory,
  MdClose,
  MdSave,
  MdLayers,
  MdFlashOn,
  MdTimeline,
  MdSecurity,
  MdChevronRight,
  MdRefresh
} from "react-icons/md";
import { 
  Subscription, 
  getCompanySubscriptions, 
  createSubscription, 
  updateSubscription, 
  deleteSubscription 
} from "../../utils/api/endpoints/companies";
import moment from "moment";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

interface SubscriptionManagerProps {
  companyId: number;
  onSync?: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ companyId, onSync }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    subscription_name: "",
    subscription_type: "monthly",
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().add(1, "month").format("YYYY-MM-DD"),
    quick_runs_quota: 10,
    comprehensive_runs_quota: 2,
    quick_run_duration: 15,
    comprehensive_run_duration: 30
  });

  const fetchSubscriptions = async () => {
    setLoading(true);
    const data = await getCompanySubscriptions(companyId);
    setSubscriptions(data.sort((a, b) => moment(b.start_date).diff(moment(a.start_date))));
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [companyId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateSubscription(companyId, editingId, formData);
        toast.success("Lifecycle Updated");
      } else {
        await createSubscription(companyId, formData);
        toast.success("New Lifecycle Established");
      }
      setIsAdding(false);
      setEditingId(null);
      fetchSubscriptions();
      if (onSync) onSync();
    } catch (err) {
      toast.error("Failed to sync record");
    }
  };

  const handleDelete = async (subId: number) => {
    if (window.confirm("Are you sure you want to purge this subscription record? This action is permanent.")) {
      const success = await deleteSubscription(companyId, subId);
      if (success) {
        fetchSubscriptions();
        toast.success("Record purged from history");
        if (onSync) onSync();
      }
    }
  };

  const startEdit = (sub: Subscription) => {
    setEditingId(sub.subscription_id);
    setFormData({
      subscription_name: sub.subscription_name || "",
      subscription_type: sub.subscription_type,
      start_date: moment(sub.start_date).format("YYYY-MM-DD"),
      end_date: moment(sub.end_date).format("YYYY-MM-DD"),
      quick_runs_quota: sub.quick_runs_quota,
      comprehensive_runs_quota: sub.comprehensive_runs_quota,
      quick_run_duration: sub.quick_run_duration,
      comprehensive_run_duration: sub.comprehensive_run_duration
    });
    setIsAdding(false);
  };

  const resetForm = () => {
    setFormData({
      subscription_name: "",
      subscription_type: "monthly",
      start_date: moment().format("YYYY-MM-DD"),
      end_date: moment().add(1, "month").format("YYYY-MM-DD"),
      quick_runs_quota: 10,
      comprehensive_runs_quota: 2,
      quick_run_duration: 15,
      comprehensive_run_duration: 30
    });
  };

  const getStatus = (start: string, end: string) => {
    const now = moment();
    const startDate = moment(start);
    const endDate = moment(end);

    if (now.isBefore(startDate)) return { label: "Upcoming", color: "text-blue-500 bg-blue-50 border-blue-100", dot: "bg-blue-400" };
    if (now.isAfter(endDate)) return { label: "Expired", color: "text-gray-400 bg-gray-50 border-gray-100", dot: "bg-gray-300" };
    return { label: "Active", color: "text-emerald-500 bg-emerald-50 border-emerald-100", dot: "bg-emerald-400" };
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 gap-4">
        <div className="w-8 h-8 border-3 border-gray-100 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading history...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[400px]">
      {/* SECTION HEADER */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white/95 backdrop-blur-sm z-20">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <MdHistory className="text-blue-600 text-lg" />
            Subscription Lifecycle
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Audit trail and quota configuration history.</p>
        </div>
        {!isAdding && editingId === null && (
          <button
            onClick={() => {
              resetForm();
              setIsAdding(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors shadow-sm"
          >
            <MdAdd className="text-lg" />
            New Lifecycle
          </button>
        )}
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {(isAdding || editingId !== null) ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-8 bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <MdAssignment className="text-blue-600" />
                  {editingId ? "Edit Configuration" : "New Subscription Term"}
                </h4>
                <button 
                  onClick={() => { setIsAdding(false); setEditingId(null); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MdClose className="text-xl" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Subscription Name</label>
                  <input
                    type="text"
                    name="subscription_name"
                    value={formData.subscription_name}
                    onChange={handleInputChange}
                    placeholder="Enter custom subscription name (e.g. Acme Corp Enterprise Subscription)"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none font-semibold text-slate-900"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Plan Structure</label>
                    <select
                      name="subscription_type"
                      value={formData.subscription_type}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half_yearly">Half-Yearly</option>
                      <option value="yearly">Yearly</option>
                      <option value="pilot">Pilot</option>
                      <option value="one-off">One-off</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Effective Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Termination Date</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1.5">
                      <MdSpeed className="text-green-500" /> Quick runs
                    </label>
                    <input
                      type="number"
                      name="quick_runs_quota"
                      value={formData.quick_runs_quota}
                      onChange={handleNumberChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1.5">
                      <MdLayers className="text-blue-500" /> Comprehensive
                    </label>
                    <input
                      type="number"
                      name="comprehensive_runs_quota"
                      value={formData.comprehensive_runs_quota}
                      onChange={handleNumberChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1.5">
                      <MdTimelapse className="text-green-400" /> Quick Dur.
                    </label>
                    <input
                      type="number"
                      name="quick_run_duration"
                      value={formData.quick_run_duration}
                      onChange={handleNumberChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1.5">
                      <MdTimelapse className="text-blue-400" /> Comp. Dur.
                    </label>
                    <input
                      type="number"
                      name="comprehensive_run_duration"
                      value={formData.comprehensive_run_duration}
                      onChange={handleNumberChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setIsAdding(false); setEditingId(null); }}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black transition-colors shadow-sm"
                  >
                    <MdSave />
                    {editingId ? "Update Term" : "Save lifecycle"}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div key="list" className="space-y-3">
              {subscriptions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg">
                  <MdTimeline className="text-4xl text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 font-medium">No subscription history found</p>
                </div>
              ) : (
                subscriptions.map((sub) => {
                  const status = getStatus(sub.start_date, sub.end_date);
                  return (
                    <motion.div
                      layout
                      key={sub.subscription_id}
                      className="group bg-white hover:bg-gray-50 border border-gray-200 rounded-lg p-4 transition-all"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                        {/* Status & Dates */}
                        <div className="lg:w-1/4">
                          {sub.subscription_name && (
                            <h5 className="font-bold text-slate-900 text-xs truncate mb-1" title={sub.subscription_name}>
                              {sub.subscription_name}
                            </h5>
                          )}
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1.5 ${status.color}`}>
                              <span className={`w-1 h-1 rounded-full ${status.dot}`} />
                              {status.label}
                            </span>
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-tighter bg-gray-100 px-2 py-0.5 rounded-md">
                              {sub.subscription_type.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                            <MdEvent className="text-blue-500" />
                            <span>{moment(sub.start_date).format("MMM D, YYYY")}</span>
                            <MdChevronRight className="text-gray-300" />
                            <span>{moment(sub.end_date).format("MMM D, YYYY")}</span>
                          </div>
                        </div>

                        {/* Quotas & Metrics */}
                        <div className="flex-1 lg:border-l lg:border-gray-100 lg:pl-6">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                            <div className="space-y-0.5">
                              <p className="text-[10px] font-medium text-gray-400 flex items-center gap-1">
                                <MdFlashOn className="text-emerald-500" /> QUICK
                              </p>
                              <div className="flex items-baseline gap-1">
                                <span className="text-base font-bold text-gray-900">{sub.quick_runs_quota}</span>
                                <span className="text-[10px] text-gray-500 font-medium">{sub.quick_run_duration}d</span>
                              </div>
                            </div>

                            <div className="space-y-0.5">
                              <p className="text-[10px] font-medium text-gray-400 flex items-center gap-1">
                                <MdLayers className="text-blue-500" /> COMP.
                              </p>
                              <div className="flex items-baseline gap-1">
                                <span className="text-base font-bold text-gray-900">{sub.comprehensive_runs_quota}</span>
                                <span className="text-[10px] text-gray-500 font-medium">{sub.comprehensive_run_duration}d</span>
                              </div>
                            </div>

                            <div className="hidden sm:block space-y-0.5">
                              <p className="text-[10px] font-medium text-gray-400 flex items-center gap-1">
                                <MdSecurity className="text-purple-500" /> SECURITY
                              </p>
                              <span className="text-base font-bold text-gray-900">∞</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col justify-end gap-1 border-t lg:border-t-0 pt-3 lg:pt-0 border-gray-50">
                          <button
                            onClick={() => startEdit(sub)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <MdEdit className="text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(sub.subscription_id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <MdDelete className="text-lg" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SubscriptionManager;
